/**
 * Where a record page's tabs read a record's rows from (#13370, round 36; spec section 23): an
 * injected data source, so the same tab components render a real record from the API and
 * Appearance's demo record from its seeded rows (`LayoutSetup/demoDataSource.ts`), with no request,
 * no query-cache seeding and no interception. The app reads through the API source, the context's
 * default; the Appearance editor provides its demo source around the tabs it renders.
 *
 * Round 36a covered the Overview's cards (the Interactions and Documents previews); round 36b the
 * Documents tab: the grid's search and count (`useSearchEntities`, `useViewCount`) and the List's
 * group summary, section pages and type facets (`DocumentsResults`); round 36c the other tabs'
 * record reads: the Relationships tab's contacts, the Artifacts tab's list and the Activity feed
 * (every grid tab already reads through the search); dropped in round 36d and back in round 37,
 * where every tab on the strip is previewed. The organization's configuration (the
 * document schema, document config and columns, the dashboard config, the saved views) is not a
 * record's data: it is read from the API wherever the tab is drawn, since it is what Appearance
 * configures.
 */
import { createContext, useContext } from "react";
import {
  fetchEntityDocumentGroups,
  fetchEntityDocumentTypes,
  fetchEntityDocuments,
  fetchEntitySchema,
  searchActivityFeedPage,
  type ActivityFeedPage,
  type ActivityLogSearchBody,
} from "./api";
import {
  fetchArtifactsForEntity,
  type ArtifactItem,
} from "@/components/FpPages/Artifacts/useArtifacts";
import { fetchContacts, type ContactsResponse } from "../Data/contacts";
import {
  fetchPerformanceMetrics,
  fetchPerformanceSeries,
  fetchPrivateFundMetrics,
  type PerformanceMetrics,
  type PerformanceSeries,
  type PrivateFundMetrics,
} from "../Data/performance";
import {
  fetchTransactions,
  type LedgerQuery,
  type TransactionsResponse,
} from "../Data/transactions";
import { fetchOverviewDocuments } from "./hooks/overviewDocuments";
import {
  fetchOverviewInteractions,
  fetchOverviewUpcoming,
} from "./hooks/overviewInteractions";
import {
  fetchViewCount,
  searchEntitiesPage,
  type SearchPage,
  type SearchParams,
  type ViewCount,
} from "../Data/search";
import type {
  EntityDocumentGroupsResponse,
  EntityDocumentsPage,
  EntityInteractionsPage,
  EntityKind,
  EntityRow,
  EntityVehicleDocumentsPage,
  RmsSchema,
} from "./types";

/** What the List asks a record's document endpoints, as the API functions take it. */
export type DocumentGroupsQuery = NonNullable<
  Parameters<typeof fetchEntityDocumentGroups>[2]
>;
export type DocumentsPageQuery = NonNullable<
  Parameters<typeof fetchEntityDocuments>[2]
>;
export type DocumentTypesQuery = NonNullable<
  Parameters<typeof fetchEntityDocumentTypes>[2]
>;

export interface RecordDataSource {
  /** Which source this is, for the query keys: a demo's rows never share a key with the API's. */
  readonly name: string;
  /**
   * The Overview's interactions preview: the newest few, the record's own when participant
   * only, of the types with these ids when any are given (#14547).
   */
  interactions: (
    kind: string,
    id: string,
    participantOnly: boolean,
    typeIds: readonly number[] | null
  ) => Promise<EntityInteractionsPage>;
  /**
   * The Overview's upcoming meetings (#12394): read apart from the past rows, the soonest few,
   * of the same types as the past rows (#14547).
   */
  upcoming: (
    kind: string,
    id: string,
    participantOnly: boolean,
    typeIds: readonly number[] | null
  ) => Promise<EntityInteractionsPage>;
  /** The Overview's documents preview: the newest few, in any section. */
  documents: (kind: string, id: string) => Promise<EntityDocumentsPage>;
  /** A grid's rows: the Documents tab's Table, scoped to the record. */
  search: (params: SearchParams, signal?: AbortSignal) => Promise<SearchPage>;
  /** How many rows (and groups) a grid's search matches. */
  count: (params: SearchParams, signal?: AbortSignal) => Promise<ViewCount>;
  /** The Documents List's sections: one per group (and per vehicle's group), with subgroups. */
  documentGroups: (
    kind: string,
    id: string,
    query: DocumentGroupsQuery
  ) => Promise<EntityDocumentGroupsResponse>;
  /** One page of a List section's documents. */
  documentsPage: (
    kind: string,
    id: string,
    query: DocumentsPageQuery
  ) => Promise<EntityDocumentsPage | EntityVehicleDocumentsPage>;
  /** The document types present in a List section, for its type filter. */
  documentTypes: (
    kind: string,
    id: string,
    query: DocumentTypesQuery
  ) => Promise<string[]>;
  /** The Relationships tab's people: the record's contacts (round 36c). */
  contacts: (kind: EntityKind, id: string) => Promise<ContactsResponse>;
  /** The Artifacts tab's rows: the artifacts assigned to the record (round 36c). */
  artifacts: (id: string, signal?: AbortSignal) => Promise<ArtifactItem[]>;
  /** One page of the Activity tab's feed, the search scoped to the record (round 36c). */
  activity: (body: ActivityLogSearchBody) => Promise<ActivityFeedPage>;
  /** The Performance tab's return series of the record (round 36 review). */
  performanceSeries: (
    id: string,
    organizationId?: string
  ) => Promise<PerformanceSeries[]>;
  /** The Performance tab's metrics of one return type, as of a date. */
  performanceMetrics: (
    id: string,
    returnType: string,
    organizationId?: string,
    asOfDate?: string
  ) => Promise<PerformanceMetrics[]>;
  /** A vehicle's IRR, paid in and returned, and its period returns. */
  privateFundMetrics: (
    id: string,
    organizationId?: string,
    asOfDate?: string
  ) => Promise<PrivateFundMetrics>;
  /** The record's ledger: its transactions and their totals. */
  transactions: (
    kind: EntityKind,
    id: string,
    query: LedgerQuery
  ) => Promise<TransactionsResponse>;
  /** A schema read for one record (a dataset tab's, whose fixed column names the record). */
  recordSchema: (
    kind: string,
    id: string,
    organizationId?: string,
    flat?: boolean
  ) => Promise<RmsSchema>;
}

/**
 * The record's rows as the server has them: the source every record page reads through. The
 * search functions are reached when called, not when this module loads: the search module reads
 * this context, so each holds the other's functions only by name.
 */
export const apiRecordDataSource: RecordDataSource = {
  name: "api",
  interactions: fetchOverviewInteractions,
  upcoming: fetchOverviewUpcoming,
  documents: fetchOverviewDocuments,
  search: (params, signal) => searchEntitiesPage(params, signal),
  count: (params, signal) => fetchViewCount(params, signal),
  documentGroups: (kind, id, query) =>
    fetchEntityDocumentGroups(kind, id, query),
  documentsPage: (kind, id, query) => fetchEntityDocuments(kind, id, query),
  documentTypes: (kind, id, query) => fetchEntityDocumentTypes(kind, id, query),
  contacts: (kind, id) => fetchContacts(kind, id),
  artifacts: (id, signal) => fetchArtifactsForEntity(id, signal),
  activity: (body) => searchActivityFeedPage(body),
  performanceSeries: (id, organizationId) =>
    fetchPerformanceSeries(id, organizationId),
  performanceMetrics: (id, returnType, organizationId, asOfDate) =>
    fetchPerformanceMetrics(id, returnType, organizationId, asOfDate),
  privateFundMetrics: (id, organizationId, asOfDate) =>
    fetchPrivateFundMetrics(id, organizationId, asOfDate),
  transactions: (kind, id, query) => fetchTransactions(kind, id, query),
  recordSchema: (kind, id, organizationId, flat) =>
    fetchEntitySchema(kind, organizationId, flat, id),
};

export const RecordDataSourceContext =
  createContext<RecordDataSource>(apiRecordDataSource);

/** The data source the tab reads through: the API's, unless a host provides another. */
export const useRecordDataSource = (): RecordDataSource =>
  useContext(RecordDataSourceContext);

/**
 * A query key as a source reads it: the API's keys unchanged (shared with the page's prefetch,
 * its probes and its invalidations), another source's with its name appended, so a demo row
 * never answers a record page's key.
 */
export const keyForSource = (
  source: RecordDataSource,
  key: readonly unknown[]
): readonly unknown[] =>
  source.name === apiRecordDataSource.name
    ? key
    : [...key, { source: source.name }];
